import React, { Suspense, useLayoutEffect, useState } from 'react'

import { TypedDocumentNode, useSuspenseQuery } from '@apollo/client'
import { Box, EnhancedDimension } from '@factorialco/deprecated-design-system'
import { Widget as WidgetContainer, WidgetStrip } from '@factorialco/f0-react/dist/experimental'

import ErrorBoundary from 'modules/core/components/ErrorBoundary'

import { FinanceWidgetStripSkeleton } from './FinanceWidgetStripSkeleton'

export type WidgetQueryArgs = Record<string, any>

type IntersectionOf<T> = (T extends WidgetQueryArgs ? (x: T) => any : never) extends (
  x: infer R
) => any
  ? R
  : never

// Utility type to extract the `TVariables` from `WidgetParams` and then compute the intersection
type ExtractCommonArgs<
  TQuery,
  TPrepared,
  TWidgets extends ReadonlyArray<WidgetParams<TQuery, TPrepared, WidgetQueryArgs>>
> = IntersectionOf<
  TWidgets[number] extends WidgetParams<TQuery, TPrepared, WidgetQueryArgs>
    ? WidgetQueryArgs
    : never
>

export interface WidgetParams<TQuery, TPrepared, TVariables extends WidgetQueryArgs> {
  id: string
  component: React.FC<{ data: TPrepared }>
  queryArgs?: TVariables
  document: TypedDocumentNode<TQuery, TVariables>
  prepareData?: (data: TQuery) => Promise<TPrepared> | TPrepared
  isEmpty?: (data: TPrepared) => boolean
  emptyComponent?: React.FC<{ data: TPrepared }>
}

export function buildWidget<TQuery, TPrepared, TVariables extends WidgetQueryArgs>(
  widgetParams: WidgetParams<TQuery, TPrepared, TVariables>
): WidgetParams<TQuery, TPrepared, TVariables> {
  return widgetParams
}

function Widget<TQuery, TVariables extends WidgetQueryArgs, TPrepared = TQuery>(
  props: Readonly<{
    widget: WidgetParams<TQuery, TPrepared, TVariables>
    queryArgs?: TVariables
  }>
) {
  const { widget, queryArgs } = props
  const { document, component: Component, prepareData, isEmpty: calcIsEmpty } = widget
  const [preparedData, setPreparedData] = useState<TPrepared | null>(null)
  const isEmpty = preparedData ? calcIsEmpty?.(preparedData) : false

  const { data } = useSuspenseQuery(document, { variables: queryArgs })

  useLayoutEffect(() => {
    if (data && prepareData) {
      Promise.resolve(prepareData(data)).then(setPreparedData)
    }
  }, [data, prepareData])

  if (!preparedData) return <WidgetContainer.Skeleton />
  if (isEmpty) {
    if (widget.emptyComponent) {
      return <widget.emptyComponent data={preparedData} />
    }

    return null
  }

  return <Component data={preparedData} />
}

export function WidgetDashboard<TWidgets extends ReadonlyArray<WidgetParams<any, any, any>>>({
  widgets,
  args,
}: Readonly<{
  widgets: TWidgets
  args?: ExtractCommonArgs<any, any, TWidgets>
}>) {
  return (
    <Box flexDirection="row" gap="s16" flexWrap="wrap">
      {[
        ...widgets.map((widget) => (
          <ErrorBoundary customCrashComponent={<></>} key={widget.id}>
            <Box minWidth="s600" style={{ flex: '1 1 0' }}>
              <Widget
                widget={widget}
                queryArgs={{ ...(args || {}), ...(widget.queryArgs || {}) }}
              />
            </Box>
          </ErrorBoundary>
        )),
      ]}
    </Box>
  )
}

export function DataTableWidgetInsights<TWidgets extends WidgetParams<any, any, any>[]>({
  widgets,
  useBox = false,
  widgetWidth,
}: Readonly<{
  widgets: TWidgets
  useBox?: boolean
  widgetWidth?: EnhancedDimension
}>) {
  const wrappedWidgets = [
    ...widgets.map((widget) => (
      <ErrorBoundary customCrashComponent={<></>} key={widget.id}>
        <Box width={widgetWidth}>
          <Widget widget={widget} queryArgs={widget.queryArgs} />
        </Box>
      </ErrorBoundary>
    )),
  ]

  return (
    <Suspense fallback={<FinanceWidgetStripSkeleton numElements={widgets.length} />}>
      {wrappedWidgets.length > 2 && !useBox ? (
        <WidgetStrip>{wrappedWidgets}</WidgetStrip>
      ) : (
        <Box flexDirection="row" width="full" gap="s16">
          {wrappedWidgets}
        </Box>
      )}
    </Suspense>
  )
}
